import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  Tab,
  Tabs,
  TextField,
  Typography,
  tabsClasses,
  useTheme,
} from "@mui/material";
import { useFormik } from "formik";
import { useCreateRestaurantMutation } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { useStyles } from "../../../theme/appStyles";
import CloseIcon from "@mui/icons-material/Close";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import {
  formatPhoneNumberForDisplay,
  validateEmail,
  validatePhoneNumber,
} from "../../../Components/Validation/validationUtils";
import { useNavigate } from "react-router-dom";

enum Section {
  General = "General",
  TimeSlot = "TimeSlot",
}

const Index = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [newDepartment, { isLoading }] = useCreateRestaurantMutation();
  const { showErrorMessage, showMessage } = useNotifier();
  const [activeSection, setActiveSection] = useState<Section>(Section.General);
  const handleContainerChange = (section: Section) => {
    setActiveSection(section);
  };

  const handlePhoneChange = (
    fieldName: string,
    value: string,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const formattedValue = formatPhoneNumberForDisplay(value);
    setFieldValue(fieldName, formattedValue);
  };

  const formik = useFormik({
    initialValues: {
      restaurantsName: "",
      restaurantsDescription: "",
      restaurantsStreetAddress: "",
      restaurantsStreetAddress2: "",
      restaurantsCity: "",
      restaurantsProvince: "",
      restaurantsPostalCode: "",
      restaurantsWebsite: "",
      restaurantsTakeout: "",
      restaurantsDelivery: "",
      restaurantsDeliveryFee: "",
      restaurantsMiniDeliveryAmount: "",
      restaurantsFreeDeliveryAmount: "",
      restaurantsDeliveryRadius: "",
      restaurantsPhoneNumber: "",
      restaurantsAltPhoneNumber: "",
      restaurantsEmail: "",
      availability: [
        {
          day: "Sunday",
          timeSlots: [],
        },
        {
          day: "Monday",
          timeSlots: [],
        },
        {
          day: "Tuesday",
          timeSlots: [],
        },
        {
          day: "Wednesday",
          timeSlots: [],
        },
        {
          day: "Thursday",
          timeSlots: [],
        },
        {
          day: "Friday",
          timeSlots: [],
        },
        {
          day: "Saturday",
          timeSlots: [],
        },
      ],
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors: { [key: string]: string } = {};

      const emailError = validateEmail(values.restaurantsEmail);
      if (emailError) {
        errors.restaurantsEmail = emailError;
      }

      const mobileNumberError = validatePhoneNumber(
        values.restaurantsPhoneNumber
      );
      if (mobileNumberError) {
        errors.restaurantsPhoneNumber = mobileNumberError;
      }

      const altMobileNumberError = validatePhoneNumber(
        values.restaurantsAltPhoneNumber
      );
      if (altMobileNumberError) {
        errors.restaurantsAltPhoneNumber = altMobileNumberError;
      }

      return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        const temData = {
          restaurantsName: values.restaurantsName,
          restaurantsDescription: values.restaurantsDescription,
          restaurantsStreetAddress: values.restaurantsStreetAddress,
          restaurantsStreetAddress2: values.restaurantsStreetAddress2,
          restaurantsCity: values.restaurantsCity,
          restaurantsProvince: values.restaurantsProvince,
          restaurantsPostalCode: values.restaurantsPostalCode,
          restaurantsWebsite: values.restaurantsWebsite,
          restaurantsTakeout: values.restaurantsTakeout,
          restaurantsDelivery: values.restaurantsDelivery,
          restaurantsDeliveryFee: values.restaurantsDeliveryFee,
          restaurantsMiniDeliveryAmount: values.restaurantsMiniDeliveryAmount,
          restaurantsFreeDeliveryAmount: values.restaurantsFreeDeliveryAmount,
          restaurantsDeliveryRadius: values.restaurantsDeliveryRadius,
          restaurantsPhoneNumber: values.restaurantsPhoneNumber.replace(
            /-/g,
            ""
          ),
          restaurantsAltPhoneNumber: values.restaurantsAltPhoneNumber.replace(
            /-/g,
            ""
          ),
          restaurantsEmail: values.restaurantsEmail,
          availability: values.availability,
        };

        const addCompanyResponse = await newDepartment(temData).unwrap();
        if (!addCompanyResponse.status) {
          showErrorMessage(addCompanyResponse.message);
        } else {
          showMessage(addCompanyResponse.message);
          resetForm();
          navigate("/branch/list");
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const formValid = useMemo(() => {
    return (
      formik.values.restaurantsName !== "" &&
      formik.values.restaurantsName !== undefined
    );
  }, [formik]);

  const renderContent = () => {
    switch (activeSection) {
      case Section.General:
        return (
          <div>
            <form onSubmit={formik.handleSubmit}>
              <Grid container spacing={2}>
                <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
                  <Grid container spacing={1}>
                    <Grid item lg={3} md={3} sm={12} xs={12}>
                      <Typography variant="subtitle1">
                        Name <span className={classes.requiedfield}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item lg={6} md={6} sm={12} xs={12}>
                      <TextField
                        placeholder="Restaurant Name"
                        size="small"
                        {...formik.getFieldProps("restaurantsName")}
                        className={classes.textField}
                        InputProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
                  <Grid container spacing={1}>
                    <Grid item lg={3} md={3} sm={12} xs={12}>
                      <Typography variant="subtitle1">Phone Number</Typography>
                    </Grid>
                    <Grid item lg={3} md={3} sm={12} xs={12}>
                      <TextField
                        placeholder="Phone Number"
                        size="small"
                        value={formik.values.restaurantsPhoneNumber}
                        onChange={(e) =>
                          handlePhoneChange(
                            "restaurantsPhoneNumber",
                            e.target.value,
                            formik.setFieldValue
                          )
                        }
                        fullWidth
                        onBlur={formik.handleBlur}
                        InputProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        error={
                          formik.touched.restaurantsPhoneNumber &&
                          Boolean(formik.errors.restaurantsPhoneNumber)
                        }
                        helperText={
                          formik.touched.restaurantsPhoneNumber &&
                          formik.errors.restaurantsPhoneNumber
                        }
                      />
                    </Grid>
                    <Grid item lg={3} md={3} sm={12} xs={12}>
                      <TextField
                        placeholder="Alt Phone Number"
                        size="small"
                        value={formik.values.restaurantsAltPhoneNumber}
                        onChange={(e) =>
                          handlePhoneChange(
                            "restaurantsAltPhoneNumber",
                            e.target.value,
                            formik.setFieldValue
                          )
                        }
                        onBlur={formik.handleBlur}
                        className={classes.textField}
                        fullWidth
                        InputProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        error={
                          formik.touched.restaurantsAltPhoneNumber &&
                          Boolean(formik.errors.restaurantsAltPhoneNumber)
                        }
                        helperText={
                          formik.touched.restaurantsAltPhoneNumber &&
                          formik.errors.restaurantsAltPhoneNumber
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
                  <Grid container spacing={1}>
                    <Grid item lg={3} md={3} sm={12} xs={12}>
                      <Typography variant="subtitle1">E-mail</Typography>
                    </Grid>
                    <Grid item lg={3} md={3} sm={12} xs={12}>
                      <TextField
                        placeholder="E-mail"
                        size="small"
                        type="email"
                        {...formik.getFieldProps("restaurantsEmail")}
                        className={classes.textField}
                        InputProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        error={
                          formik.touched.restaurantsEmail &&
                          Boolean(formik.errors.restaurantsEmail)
                        }
                        helperText={
                          formik.touched.restaurantsEmail &&
                          formik.errors.restaurantsEmail
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
                  <Grid container spacing={1}>
                    <Grid item lg={3} md={3} sm={12} xs={12}>
                      <Typography variant="subtitle1">
                        Street Address
                      </Typography>
                    </Grid>
                    <Grid item lg={6} md={6} sm={12} xs={12}>
                      <TextField
                        placeholder="Street Address"
                        size="small"
                        {...formik.getFieldProps("restaurantsStreetAddress")}
                        className={classes.textField}
                        InputProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
                  <Grid container spacing={1}>
                    <Grid item lg={3} md={3} sm={12} xs={12}>
                      <Typography variant="subtitle1">
                        Alt Street Address
                      </Typography>
                    </Grid>
                    <Grid item lg={6} md={6} sm={12} xs={12}>
                      <TextField
                        placeholder="Alt Street Address"
                        size="small"
                        {...formik.getFieldProps("restaurantsStreetAddress2")}
                        className={classes.textField}
                        InputProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
                  <Grid container spacing={1}>
                    <Grid item lg={3} md={3} sm={12} xs={12}>
                      <Typography variant="subtitle1">Address</Typography>
                    </Grid>
                    <Grid item lg={2} md={2} sm={12} xs={12}>
                      <TextField
                        placeholder="City"
                        size="small"
                        {...formik.getFieldProps("restaurantsCity")}
                        className={classes.textField}
                        InputProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item lg={2} md={2} sm={12} xs={12}>
                      <TextField
                        placeholder="Province"
                        size="small"
                        {...formik.getFieldProps("restaurantsProvince")}
                        className={classes.textField}
                        InputProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item lg={2} md={2} sm={12} xs={12}>
                      <TextField
                        placeholder="PostalCode"
                        size="small"
                        {...formik.getFieldProps("restaurantsPostalCode")}
                        className={classes.textField}
                        InputProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
                  <Grid container spacing={1}>
                    <Grid item lg={3} md={3} sm={12} xs={12}>
                      <Typography variant="subtitle1">Website</Typography>
                    </Grid>
                    <Grid item lg={6} md={6} sm={12} xs={12}>
                      <TextField
                        placeholder="Website"
                        size="small"
                        {...formik.getFieldProps("restaurantsWebsite")}
                        className={classes.textField}
                        InputProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
                  <Grid container spacing={1}>
                    <Grid item lg={3} md={3} sm={12} xs={12}>
                      <Typography variant="subtitle1">Takeout</Typography>
                    </Grid>
                    <Grid item lg={6} md={6} sm={12} xs={12}>
                      <TextField
                        placeholder="Takeout"
                        size="small"
                        {...formik.getFieldProps("restaurantsTakeout")}
                        className={classes.textField}
                        InputProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
                  <Grid container spacing={1}>
                    <Grid item lg={3} md={3} sm={12} xs={12}>
                      <Typography variant="subtitle1">Delivery</Typography>
                    </Grid>
                    <Grid item lg={6} md={6} sm={12} xs={12}>
                      <TextField
                        placeholder="Delivery"
                        size="small"
                        {...formik.getFieldProps("restaurantsDelivery")}
                        className={classes.textField}
                        InputProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
                  <Grid container spacing={1}>
                    <Grid item lg={3} md={3} sm={12} xs={12}>
                      <Typography variant="subtitle1">Delivery Fee</Typography>
                    </Grid>
                    <Grid item lg={3} md={3} sm={12} xs={12}>
                      <TextField
                        placeholder="Delivery Fee"
                        size="small"
                        type="number"
                        {...formik.getFieldProps("restaurantsDeliveryFee")}
                        className={classes.textField}
                        InputProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
                  <Grid container spacing={1}>
                    <Grid item lg={3} md={3} sm={12} xs={12}>
                      <Typography variant="subtitle1">
                        Mini Delivery Amount
                      </Typography>
                    </Grid>
                    <Grid item lg={3} md={3} sm={12} xs={12}>
                      <TextField
                        placeholder="Mini Delivery Amount"
                        size="small"
                        type="number"
                        {...formik.getFieldProps(
                          "restaurantsMiniDeliveryAmount"
                        )}
                        className={classes.textField}
                        InputProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
                  <Grid container spacing={1}>
                    <Grid item lg={3} md={3} sm={12} xs={12}>
                      <Typography variant="subtitle1">
                        Free Delivery Amount
                      </Typography>
                    </Grid>
                    <Grid item lg={3} md={3} sm={12} xs={12}>
                      <TextField
                        placeholder="Free Delivery Amount"
                        size="small"
                        type="number"
                        {...formik.getFieldProps(
                          "restaurantsFreeDeliveryAmount"
                        )}
                        className={classes.textField}
                        InputProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
                  <Grid container spacing={1}>
                    <Grid item lg={3} md={3} sm={12} xs={12}>
                      <Typography variant="subtitle1">Description</Typography>
                    </Grid>
                    <Grid item lg={6} md={6} sm={12} xs={12}>
                      <TextField
                        placeholder="Description"
                        size="small"
                        multiline
                        rows={8}
                        {...formik.getFieldProps("restaurantsDescription")}
                        className={classes.textField}
                        InputProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            fontSize: 14,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item lg={12} md={12} sm={12} xs={12} pt={3}>
                  <Card className={classes.card}>
                    <Grid container spacing={3} p={"24px"}>
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        sx={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        <Button
                          variant="outlined"
                          startIcon={<CloseIcon />}
                          className={classes.addButton}
                          onClick={() => formik.resetForm()}
                        >
                          Reset
                        </Button>
                        <Box m={0.5}></Box>

                        <Button
                          variant="contained"
                          startIcon={
                            isLoading ? (
                              <CircularProgress
                                size={18}
                                className={classes.isLoading}
                              />
                            ) : (
                              <SaveAltIcon />
                            )
                          }
                          className={classes.addButton}
                          onClick={() => formik.handleSubmit()}
                          disabled={!formValid || isLoading}
                        >
                          Save
                        </Button>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>
              </Grid>
            </form>
          </div>
        );
      case Section.TimeSlot:
        return (
          <Grid container spacing={2}>
            <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
              TimeSlot
            </Grid>
          </Grid>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      <Grid container spacing={3}>
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <Grid container spacing={2}>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card className={classes.card}>
                <Grid
                  container
                  justifyContent={"space-between"}
                  spacing={5}
                  className={classes.titleGrid}
                >
                  <Grid
                    item
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    pt={2}
                    alignContent={"center"}
                  >
                    <Box>
                      <Tabs
                        variant="scrollable"
                        value={activeSection}
                        onChange={(_, newValue) =>
                          handleContainerChange(newValue)
                        }
                        aria-label="scrollable auto tabs example"
                        sx={{
                          [`& .${tabsClasses.scrollButtons}`]: {
                            "&.Mui-disabled": { opacity: 0.3 },
                          },
                          "& .MuiTabs-scrollButtons svg": {
                            fontSize: 40,
                            color: "black",
                          },
                          borderBottom: "1px solid #ccc",
                          px: 2,
                          pt: 3,
                        }}
                      >
                        <Tab
                          className={classes.cardTitleTab}
                          value={Section.General}
                          label="General"
                        />
                        <Tab
                          className={classes.cardTitleTab}
                          value={Section.TimeSlot}
                          label="TimeSlot"
                        />
                      </Tabs>
                    </Box>
                  </Grid>
                </Grid>

                <Grid container spacing={3} p={"24px"}>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    {renderContent()}
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default Index;
